import queryString from "query-string";
import * as R from "ramda";
import { BpmDateString, lessThanIgnoreTime } from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import {
  Forecast,
  ForecastGroup,
  ForecastKpi,
  OutcomeVariableType
} from "@blisspointmedia/bpm-types/dist/Forecasting";
import {
  ModelOverviewRow,
  ModelOverviewSection
} from "../Components/DynamicModelOverview/DynamicModelOverview";
import { dateToMddyy, dateToMddyyDashed } from "../utils/date-utils";
import { downloadJSONToCSV } from "../utils/download-utils";
import {
  formatDecimal,
  formatMoney,
  formatMoneyAsInt,
  formatNumberAsInt,
  formatPercent
} from "../utils/format-utils";
import {
  BestModelRow,
  FoldDataRow,
  ForecastChartViewLabels,
  ForecastData,
  ForecastKpiViewOption,
  FORECASTS_S3_FOLDERS,
  formatCpm,
  formatSpend,
  FullForecastSelection,
  HISTORICAL_KEY,
  KPI_CI_VIEW_SUFFICES,
  KPI_VIEW_SUFFICES,
  KpiOutcomeString,
  ModelSummaryData,
  NA_VALUE,
  OUTCOME_VARIABLE_LABELS,
  SelectableForecastProperty,
  TrainTestData
} from "./forecastingConstants";

export const formatFoldData: (
  foldData: FoldDataRow[] | undefined
) => Record<KpiOutcomeString, TrainTestData[]> = foldData => {
  const output = {};

  if (!foldData) {
    return output;
  }

  const foldDataByKpiOutcome: Record<KpiOutcomeString, FoldDataRow[]> = R.groupBy(row => {
    const capitalizedOutcome = (String(row.outcome).charAt(0).toUpperCase() +
      String(row.outcome).slice(1)) as "Volume" | "Revenue";

    return `${row.kpi}, ${capitalizedOutcome}`;
  }, foldData);

  Object.keys(foldDataByKpiOutcome).forEach(kpiKey => {
    const selectedFoldData: FoldDataRow[] = foldDataByKpiOutcome[kpiKey];

    const uniqCutoffDates = new Set(selectedFoldData.map(row => row.cutoff));
    if (uniqCutoffDates.size !== 1) {
      throw new Error(
        `Expected exactly 1 cutoff date for ${kpiKey}, found ${uniqCutoffDates.size}`
      );
    }

    const formattedFoldData = selectedFoldData.map(row => ({
      date: row.ds,
      actual_kpi: row.y,
      predicted_kpi: row.yhat,
      predicted_kpi_ci: [row.yhat_lower, row.yhat_upper],
      predicted_efficiency: row.efficiency_estimate,
      predicted_efficiency_ci: [row.efficiency_estimate_lower, row.efficiency_estimate_upper],
      train_test: lessThanIgnoreTime(row.cutoff, row.ds) ? "test" : "train",
      spend: row.spend,
      cpm: row.cpm,
    }));

    output[kpiKey] = formattedFoldData;
  });

  return output;
};

export const formatModelOverviewData: (
  modelSummaryData: ModelSummaryData | undefined,
  kpiType: OutcomeVariableType | undefined,
  primaryBudget: number | undefined,
  lastRefreshDate: BpmDateString | undefined
) => ModelOverviewSection[] = (modelSummaryData, kpiType, primaryBudget, lastRefreshDate) => {
  if (!modelSummaryData || !kpiType || !primaryBudget || !lastRefreshDate) {
    return [];
  }

  const {
    total_estimate,
    total_estimate_lower,
    total_estimate_upper,
    total_cpa: efficiency_estimate,
    total_cpa_upper: efficiency_estimate_lower,
    total_cpa_lower: efficiency_estimate_upper,
    hist_start,
    hist_end,
    avg_spend,
    model,
    last_fold_mape,
    last_fold_mae,
    avg_MAPE,
    avg_MAE,
    test,
    train,
  } = modelSummaryData;

  const formatTotalKpi = getKpiFormatter(kpiType, ForecastKpiViewOption.TOTAL);
  const formatEfficiencyKpi = getKpiFormatter(kpiType, ForecastKpiViewOption.TOTAL);
  const keyForecastTotalValue = (
    <div>
      {formatTotalKpi(total_estimate)}{" "}
      <span className="confidenceRange">
        ({formatTotalKpi(total_estimate_lower)}&ndash;{formatTotalKpi(total_estimate_upper)})
      </span>
    </div>
  );

  // A "better" CPA is a smaller number, which we want to show first in the range for UX understanding
  const lowEffCi = kpiType === "Revenue" ? efficiency_estimate_lower : efficiency_estimate_upper;
  const highEffCi = kpiType === "Revenue" ? efficiency_estimate_upper : efficiency_estimate_lower;
  const keyForecastEfficiencyValue = (
    <div>
      {formatEfficiencyKpi(efficiency_estimate)}{" "}
      <span className="confidenceRange">
        ({formatEfficiencyKpi(lowEffCi)}&ndash;
        {formatEfficiencyKpi(highEffCi)})
      </span>
    </div>
  );

  let keyForecastRows: ModelOverviewRow[] = [
    {
      label: ForecastChartViewLabels[kpiType][ForecastKpiViewOption.TOTAL],
      value: keyForecastTotalValue,
    },
    {
      label: ForecastChartViewLabels[kpiType][ForecastKpiViewOption.EFFICIENCY],
      value: keyForecastEfficiencyValue,
    },
  ];

  return [
    {
      title: "Training Data",
      rows: [
        { label: "First model observation", value: dateToMddyy(hist_start) },
        { label: "Last model observation", value: dateToMddyy(hist_end) },
        { label: "Average daily spend", value: formatMoneyAsInt(avg_spend) },
        { label: "Outcome variable", value: OUTCOME_VARIABLE_LABELS[kpiType] },
      ],
    },
    {
      title: "Recency",
      rows: [{ label: "Last refresh date", value: dateToMddyy(lastRefreshDate) }],
    },
    {
      title: "Statistics",
      rows: [
        { label: "Spend Relationship", value: model },
        {
          label: "MAPE",
          value: formatDecimal(last_fold_mape, 2),
          tooltipText:
            "Mean absolute percentage error is a measure of the model's prediction accuracy, reflecting the average magnitude of the difference between actual and predicted values in percentage terms. Its definition is avg(abs((predicted - actual)/actual)).",
        },
        {
          label: "MAE",
          value: formatDecimal(last_fold_mae, 2),
          tooltipText:
            "Mean absolute error is a measure of the model's prediction accuracy, reflecting the average magnitude of the difference between actual and predicted values in absolute terms. Its definition is avg(abs(predicted - actual)).",
        },
      ],
      subsections: [
        [
          {
            label: "Train/Test Split",
            value: `${Math.round(train)}/${Math.round(test)}`,
            tooltipText:
              "This refers to the fraction of data used to fit the model vs the fraction held out for model validation. Setting aside data for model validation is very important in ensuring that the model generalizes well to new, unseen data.",
          },
          { label: "Median MAPE", value: formatDecimal(avg_MAPE, 2) },
          { label: "Median MAE", value: formatDecimal(avg_MAE, 2) },
        ],
      ],
    },
    {
      title: `Key Forecasts for ${formatMoneyAsInt(primaryBudget)}`,
      rows: keyForecastRows,
    },
  ];
};

export const formatBestModelData: (
  bestModelData: BestModelRow[],
  comparisonBudgets: string[]
) => Record<KpiOutcomeString, ForecastData<typeof comparisonBudgets[number]>[]> = (
  bestModelData,
  comparisonBudgets
) => {
  const output: Record<KpiOutcomeString, ForecastData<typeof comparisonBudgets[number]>[]> = {};

  const bestModelByKpi: Record<KpiOutcomeString, BestModelRow[]> = R.groupBy(row => {
    const capitalizedOutcome = (String(row.outcome).charAt(0).toUpperCase() +
      String(row.outcome).slice(1)) as "Volume" | "Revenue";

    return `${row.kpi}, ${capitalizedOutcome}`;
  }, bestModelData);

  Object.keys(bestModelByKpi).forEach(kpiKey => {
    const bestModelRows: BestModelRow[] = bestModelByKpi[kpiKey];
    const allBudgetRows = R.groupBy(row => row.ds, bestModelRows);

    const allBudgetsByDate: Record<BpmDateString, any> = {};
    Object.keys(allBudgetRows).forEach(dateKey => {
      const rowsForDate: BestModelRow[] = allBudgetRows[dateKey];

      allBudgetsByDate[dateKey] = {
        date: dateKey,
        timestamp: new Date(dateKey).getTime(),
      };

      rowsForDate.forEach(
        ({
          budget,
          yhat,
          yhat_lower,
          yhat_upper,
          efficiency_estimate,
          efficiency_estimate_lower,
          efficiency_estimate_upper,
          spend,
          cpm,
        }) => {
          allBudgetsByDate[dateKey] = {
            ...allBudgetsByDate[dateKey],
            [`${budget}-${KPI_VIEW_SUFFICES.Total}`]: yhat,
            [`${budget}-${KPI_CI_VIEW_SUFFICES.Total}`]: [yhat_lower, yhat_upper],
            [`${budget}-${KPI_VIEW_SUFFICES.Efficiency}`]: efficiency_estimate,
            [`${budget}-${KPI_CI_VIEW_SUFFICES.Efficiency}`]: [
              efficiency_estimate_lower,
              efficiency_estimate_upper,
            ],
            [`${budget}-spend`]: spend,
            [`${budget}-cpm`]: cpm,
          };
        }
      );
    });

    output[kpiKey] = Object.values(allBudgetsByDate);
  });

  return output;
};

export const combineHistoricalPredictedData: (
  modelResultsData: Record<KpiOutcomeString, TrainTestData[]>,
  predictedData: Record<KpiOutcomeString, ForecastData<string>[]>
) => [Record<KpiOutcomeString, ForecastData<string>[]>, BpmDateString] = (
  modelResultsData,
  predictedData
) => {
  const output: Record<KpiOutcomeString, ForecastData<string>[]> = {};
  let earliestDate;

  // Ensure both the historical and forecasted data share the same set of kpis
  const kpiOutcomeKeys = new Set([
    ...(Object.keys(modelResultsData) as (`${string}, Volume` | `${string}, Revenue`)[]),
    ...(Object.keys(predictedData) as (`${string}, Volume` | `${string}, Revenue`)[]),
  ]);

  if (
    !modelResultsData ||
    !predictedData ||
    kpiOutcomeKeys.size === 0 ||
    Object.keys(modelResultsData).length !== kpiOutcomeKeys.size ||
    Object.keys(predictedData).length !== kpiOutcomeKeys.size
  ) {
    return [output, earliestDate];
  }

  kpiOutcomeKeys.forEach(key => {
    const predictedDataForKpi = predictedData[key];
    const earliestPredictedDate = predictedDataForKpi.map(row => row.date).sort()[0];
    // Theoretically, all kpis that are part of the same forecast should have the same date range,
    // So it shouldn't matter which value earliestDate is assigned as in the end
    earliestDate = earliestPredictedDate;

    const historicalData: TrainTestData[] = modelResultsData[key].filter(row =>
      lessThanIgnoreTime(row.date, earliestPredictedDate)
    );

    const formattedHistoricalData = historicalData.map(({ date, actual_kpi, spend, cpm }) => {
      // CPA = Spend / KPI Volume, ROAS = Revenue / Spend
      const actual_efficency = key.endsWith("Volume") ? spend / actual_kpi : actual_kpi / spend;

      return {
        date,
        timestamp: new Date(date).getTime(),
        [`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES.Total}`]: actual_kpi,
        [`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES.Efficiency}`]: actual_efficency,
        [`${HISTORICAL_KEY}-spend`]: spend,
        [`${HISTORICAL_KEY}-cpm`]: cpm,
      };
    });

    output[key] = [...formattedHistoricalData, ...predictedDataForKpi] as ForecastData<string>[];
  });

  return [output, earliestDate];
};

interface IsForecastLastInGroupInput {
  forecastGroups: ForecastGroup[] | undefined;
  groupId: string;
  id: string;
}

export const isForecastLastInGroup = ({
  forecastGroups,
  groupId,
  id,
}: IsForecastLastInGroupInput): boolean => {
  if (!forecastGroups) {
    return false;
  }

  const foundGroup = forecastGroups.find(group => group.groupId === groupId);

  if (foundGroup) {
    const foundForecastIndex = foundGroup.forecasts.findIndex(forecast => forecast.id === id);

    if (foundForecastIndex !== -1) {
      return foundForecastIndex === foundGroup.forecasts.length - 1;
    }
  }

  return false;
};

export const getSelectablePropertyFromForecast: (
  forecast: FullForecastSelection,
  propertyName: SelectableForecastProperty
) => string | number = (forecast, propertyName) => {
  const { channel, platform, outcomeVar, kpiName, primaryBudget, startDate, endDate } = forecast;

  switch (propertyName) {
    case SelectableForecastProperty.CHANNEL_PLATFORM:
      return getCombinedChannelPlatform(channel, platform);
    case SelectableForecastProperty.OUTCOME_VARIABLE:
      return outcomeVar;
    case SelectableForecastProperty.KPI:
      return kpiName;
    case SelectableForecastProperty.BUDGET:
      return primaryBudget;
    case SelectableForecastProperty.DATE_RANGE:
      return getCombinedDateRange(startDate, endDate);
  }
};

export const buildFullForecastSelection: (
  group: ForecastGroup,
  forecast: Forecast,
  kpi: ForecastKpi
) => FullForecastSelection = (group, forecast, kpi) => {
  const forecastGroupCopy: Omit<ForecastGroup, "forecasts"> & { forecasts?: Forecast[] } = {
    ...group,
  };
  delete forecastGroupCopy.forecasts;

  const forecastCopy: Omit<Forecast, "kpis"> & { kpis?: ForecastKpi[] } = {
    ...forecast,
  };
  delete forecastCopy.kpis;

  return { ...forecastGroupCopy, ...forecastCopy, kpiName: kpi.name, outcomeVar: kpi.type };
};

export const extractForecastValsFromQueryParams = (
  search: string
): {
  forecastId: string | undefined;
  kpiName: string | undefined;
  outcomeVar: OutcomeVariableType | undefined;
} => {
  const {
    forecastId: forecastIdQueryParam,
    kpiName: kpiNameQueryParam,
    outcomeVar: outcomeVarQueryParam,
  } = queryString.parse(search);

  const forecastId = typeof forecastIdQueryParam === "string" ? forecastIdQueryParam : undefined;
  const kpiName = typeof kpiNameQueryParam === "string" ? kpiNameQueryParam : undefined;
  const outcomeVar =
    typeof outcomeVarQueryParam === "string" && ["Volume", "Revenue"].includes(outcomeVarQueryParam)
      ? (outcomeVarQueryParam as OutcomeVariableType)
      : undefined;

  return { forecastId, kpiName, outcomeVar };
};

export const getCombinedChannelPlatform = (channel: string, platform: string | null): string => {
  return `${channel}${platform ? `: ${platform}` : ""}`;
};

export const getCombinedDateRange = (startDate: BpmDateString, endDate: BpmDateString): string => {
  return `${dateToMddyy(startDate)} – ${dateToMddyy(endDate)}`;
};

interface GetS3FilenameInput {
  cid: string;
  channel: string;
  platform: string | null;
  forecastId: string;
  suffix: string;
}

export const getS3Filename = ({
  cid,
  channel,
  platform,
  forecastId,
  suffix,
}: GetS3FilenameInput): string => {
  return `${FORECASTS_S3_FOLDERS}/${cid}/${getCombinedChannelPlatform(
    channel,
    platform
  )}/${forecastId}_${suffix}.csv`;
};

export const getKpiFormatter = (
  outcomeVar: OutcomeVariableType,
  view: ForecastKpiViewOption = ForecastKpiViewOption.TOTAL
): ((value: number) => string) => {
  if (outcomeVar === "Volume" && view === ForecastKpiViewOption.TOTAL) {
    // Total KPI Volume as an int
    return formatNumberAsInt;
  } else if (outcomeVar === "Revenue" && view === ForecastKpiViewOption.TOTAL) {
    // Total Revenue KPI as a money int
    return formatMoneyAsInt;
  } else {
    // CPA and ROAS as a money decimal
    return formatMoney;
  }
};

export const getCiFormatter = (
  outcomeVar: OutcomeVariableType,
  view: ForecastKpiViewOption = ForecastKpiViewOption.TOTAL
): ((value: [number, number]) => string) => {
  const formatKpi = getKpiFormatter(outcomeVar, view);

  return val => `${formatKpi(val[0])}–${formatKpi(val[1])}`;
};

export const getForecastDownloadFilename = (
  forecast: FullForecastSelection,
  cid: string,
  prefix = "Forecast"
): string =>
  `${prefix}_${cid}_${forecast.channel}${forecast.platform ? `_${forecast.platform}` : ""}_${
    forecast.kpiName
  }_${forecast.outcomeVar}_${dateToMddyyDashed(forecast.startDate)}_to_${dateToMddyyDashed(
    forecast.endDate
  )}`.replace(/\s/g, "-");

interface TransformAndDownloadDataToCSVInput {
  forecast: FullForecastSelection;
  data: ForecastData<string>[];
  earliestPredictedDate: BpmDateString;
  includeHistorical: boolean;
  cid: string;
}

// Convert from wide format to long format for download
export const transformAndDownloadDataToCSV = ({
  forecast,
  data,
  earliestPredictedDate,
  includeHistorical,
  cid,
}: TransformAndDownloadDataToCSVInput): void => {
  const historicalData = data.filter(row => lessThanIgnoreTime(row.date, earliestPredictedDate));
  const forecastedData = data.filter(row => !lessThanIgnoreTime(row.date, earliestPredictedDate));
  const allBudgets = [forecast.primaryBudget, ...forecast.comparisonBudgets];
  const formatTotalKpi = getKpiFormatter(forecast.outcomeVar);
  const formatEfficiencyKpi = getKpiFormatter(forecast.outcomeVar);

  const kpiKey = `${ForecastChartViewLabels[forecast.outcomeVar][ForecastKpiViewOption.TOTAL]}: ${
    forecast.kpiName
  }`;
  const kpiLowerCIKey = `${kpiKey}, Lower CI`;
  const kpiUpperCIKey = `${kpiKey}, Upper CI`;
  const efficiencyKey =
    ForecastChartViewLabels[forecast.outcomeVar][ForecastKpiViewOption.EFFICIENCY];
  const efficiencyLowerCIKey = `${efficiencyKey}, Lower CI`;
  const efficiencyUpperCIKey = `${efficiencyKey}, Upper CI`;

  const formattedHistoricalData = includeHistorical
    ? historicalData.flatMap(row => ({
        date: row.date,
        "Budget Percent": HISTORICAL_KEY,
        "Budget Value": HISTORICAL_KEY,
        Spend: formatSpend(row[`${HISTORICAL_KEY}-spend`]),
        CPM: formatCpm(row[`${HISTORICAL_KEY}-cpm`]),
        [kpiKey]: formatTotalKpi(row[`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES.Total}`]),
        [kpiLowerCIKey]: NA_VALUE,
        [kpiUpperCIKey]: NA_VALUE,
        [efficiencyKey]: formatEfficiencyKpi(
          row[`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES.Efficiency}`]
        ),
        [efficiencyLowerCIKey]: NA_VALUE,
        [efficiencyUpperCIKey]: NA_VALUE,
      }))
    : [];

  const formattedForecastedData = allBudgets.flatMap(budget => {
    return forecastedData.map(row => {
      const confidenceInterval = row[`${budget}-${KPI_CI_VIEW_SUFFICES.Total}`] ?? [0, 0];
      const efficiencyConfidenceInterval = row[`${budget}-${KPI_CI_VIEW_SUFFICES.Efficiency}`] ?? [
        0,
        0,
      ];
      return {
        date: row.date,
        "Budget Percent": formatPercent(budget / forecast.primaryBudget, 0),
        "Budget Value": formatMoneyAsInt(budget),
        Spend: formatMoney(row[`${budget}-spend`]),
        CPM: formatMoney(row[`${budget}-cpm`]),
        [kpiKey]: formatTotalKpi(row[`${budget}-${KPI_VIEW_SUFFICES.Total}`]),
        [kpiLowerCIKey]: formatTotalKpi(confidenceInterval[0]),
        [kpiUpperCIKey]: formatTotalKpi(confidenceInterval[1]),
        [efficiencyKey]: formatEfficiencyKpi(row[`${budget}-${KPI_VIEW_SUFFICES.Efficiency}`]),
        [efficiencyLowerCIKey]: formatEfficiencyKpi(efficiencyConfidenceInterval[0]),
        [efficiencyUpperCIKey]: formatEfficiencyKpi(efficiencyConfidenceInterval[1]),
      };
    });
  });

  const csvRows = [...formattedHistoricalData, ...formattedForecastedData];
  const prefix = includeHistorical ? "Forecast+Historical" : "Forecast";
  const fileName = getForecastDownloadFilename(forecast, cid, prefix);

  downloadJSONToCSV(csvRows, fileName);
};
